import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { View, FlatList, StyleSheet, TouchableOpacity, Animated, ActivityIndicator, RefreshControl } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AppShell from '../components/layout/AppShell';
import ScreenCommandHeader from '../components/layout/ScreenCommandHeader';
import TopBar from '../components/layout/TopBar';
import LedgerFilters, { LedgerFilterOptions } from '../components/ledger/LedgerFilters';
import LedgerEntryCard from '../components/ledger/LedgerEntryCard';
import LedgerAccountCard from '../components/ledger/LedgerAccountCard';
import AccountFilterModal from '../components/ledger/AccountFilterModal';
import PendingInviteCard from '../components/ledger/PendingInviteCard';
import EmptyState from '../components/work/EmptyState';
import FloatingActionButton from '../components/common/FloatingActionButton';
import ThemedText from '../components/common/ThemedText';
import LedgerEntrySkeleton from '../components/common/LedgerEntrySkeleton';
import ListItemSkeleton from '../components/common/ListItemSkeleton';
import { LedgerStackParamList } from '../types/navigation';
import { LedgerEntry, LedgerFilter, Ledger, LedgerAccountFilter, LedgerTabType, LedgerInvite } from '../types/ledger';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../context/AuthContext';
import * as ledgerApi from '../lib/api/ledger';
import { storage } from '../utils/storage';
import { Alert } from '../utils/toast';

type LedgerScreenNavigationProp = StackNavigationProp<LedgerStackParamList, 'LedgerHome'>;

const LedgerScreen: React.FC = () => {
  const navigation = useNavigation<LedgerScreenNavigationProp>();
  const { theme } = useTheme();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const [selectedTab, setSelectedTab] = useState<LedgerTabType>('accounts');
  const [selectedFilter, setSelectedFilter] = useState<LedgerFilter>('all');
  const [selectedAccountFilter, setSelectedAccountFilter] = useState<LedgerAccountFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [entries, setEntries] = useState<any[]>([]);
  const [ledgers, setLedgers] = useState<any[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [filters, setFilters] = useState<LedgerFilterOptions>({
    timeframe: 'all',
    amountRange: 'all',
    transactionType: 'all',
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  const [headerHeight, setHeaderHeight] = useState(0);
  const scrollY = useRef(new Animated.Value(0)).current;
  
  const headerTranslate = useMemo(() => {
    if (headerHeight === 0) {
      return 0;
    }
    return scrollY.interpolate({
      inputRange: [0, headerHeight],
      outputRange: [0, -headerHeight],
      extrapolate: 'clamp',
    });
  }, [scrollY, headerHeight]);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: -20,
        duration: 150,
        useNativeDriver: true,
      }),
    ]).start(() => {
      slideAnim.setValue(20);
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    });
  }, [selectedFilter, selectedTab, selectedAccountFilter]);

  const loadEntries = async (loadMore: boolean = false) => {
    const token = await storage.getToken();
    if (!token) {
      console.log('No token available for loading entries');
      return;
    }
    if (loading) return;

    try {
      setLoading(true);
      const currentPage = loadMore ? page + 1 : 1;
      
      const typeFilter = selectedFilter === 'all' ? 'all' : 
                        selectedFilter === 'received' ? 'credit' : 'debit';
      
      const response = await ledgerApi.getAllTransactions(
        token,
        typeFilter,
        currentPage,
        10
      );

      if (loadMore) {
        setEntries(prev => [...prev, ...(response.transactions || [])]);
        setPage(currentPage);
      } else {
        setEntries(response.transactions || []);
        setPage(1);
      }
      
      setHasMore(response.hasMore);
    } catch (error: any) {
      console.error('Error loading ledger entries:', error);
      Alert.alert('Error', error.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadLedgers = async () => {
    const token = await storage.getToken();
    if (!token) {
      console.log('No token available for loading ledgers');
      return;
    }

    try {
      setLoading(true);
      console.log('Loading ledgers with filter:', selectedAccountFilter);
      const response = await ledgerApi.getLedgers(
        token,
        selectedAccountFilter,
        1,
        10
      );
      setHasMore(response.hasMore);
      setPage(1);
      console.log('Ledgers loaded:', response.accounts?.length || 0);
      setLedgers(response.accounts || []);
    } catch (error: any) {
      console.error('Error loading ledgers:', error);
      Alert.alert('Error', error.message || 'Failed to load ledgers');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadMoreLedgers = async () => {
    if (!hasMore || loading) return;

    const token = await storage.getToken();
    if (!token) return;

    try {
      setLoading(true);
      const nextPage = page + 1;
      const response = await ledgerApi.getLedgers(
        token,
        selectedAccountFilter,
        nextPage,
        10
      );
      setLedgers(prev => [...prev, ...(response.accounts || [])]);
      setHasMore(response.hasMore);
      setPage(nextPage);
    } catch (error: any) {
      console.error('Error loading more ledgers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInvites = async () => {
    const token = await storage.getToken();
    if (!token) {
      console.log('No token available for loading invites');
      return;
    }

    try {
      const invitesData = await ledgerApi.getPendingInvites(token);
      console.log('Invites loaded:', invitesData.length);
      setInvites(invitesData);
    } catch (error: any) {
      console.error('Error loading invites:', error);
    }
  };

  const loadData = async () => {
    if (selectedTab === 'accounts') {
      await loadLedgers();
      await loadInvites(); // Load invites when on accounts tab
    } else {
      await loadEntries(false);
    }
  };

  // Load data when screen comes into focus or when filters change
  useFocusEffect(
    useCallback(() => {
      loadData();
    }, [selectedTab, selectedFilter, selectedAccountFilter])
  );

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
  };

  const handleLoadMore = () => {
    if (selectedTab === 'transactions' && hasMore && !loading) {
      loadEntries(true);
    }
  };

  const handleEntryPress = (entry: any) => {
    // Get person name from the entry data (available in all transactions)
    const personName = entry.personName || entry.ledger?.personName || 'Unknown';
    
    // Navigate to ledger detail instead of edit screen
    navigation.navigate('LedgerDetail', { ledgerId: entry.ledgerId, personName });
  };

  const handleLedgerPress = (ledgerId: string, personName: string) => {
    navigation.navigate('LedgerDetail', { ledgerId, personName });
  };

  const handleBookmarkToggle = async (ledgerId: string) => {
    const ledger = ledgers.find(l => l.id === ledgerId);
    if (!ledger) return;

    const newBookmarkState = !ledger.isBookmarked;

    // Optimistically update UI
    setLedgers(prev => prev.map(l =>
      l.id === ledgerId
        ? { ...l, isBookmarked: newBookmarkState }
        : l
    ));

    try {
      const token = await storage.getToken();
      if (!token) {
        Alert.alert('Error', 'Please login to continue');
        // Revert the optimistic update
        setLedgers(prev => prev.map(l =>
          l.id === ledgerId
            ? { ...l, isBookmarked: !newBookmarkState }
            : l
        ));
        return;
      }

      await ledgerApi.updateLedger(token, ledgerId, {
        isBookmarked: newBookmarkState,
      });
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      Alert.alert('Error', 'Failed to update bookmark');
      // Revert the optimistic update
      setLedgers(prev => prev.map(l =>
        l.id === ledgerId
          ? { ...l, isBookmarked: !newBookmarkState }
          : l
      ));
    }
  };

  const handleAcceptInvite = async (inviteId: string) => {
    const invite = invites.find(inv => inv.id === inviteId);
    if (!invite) return;

    const token = await storage.getToken();
    if (!token) {
      Alert.alert('Error', 'Please login to continue');
      return;
    }

    try {
      setLoading(true);
      const ledger = await ledgerApi.acceptInvite(token, inviteId);

      Alert.alert(
        'Invite Accepted',
        `You accepted the ledger invite from ${invite.fromUserName}. This ledger will now appear in your accounts.`,
        [{ text: 'OK' }]
      );

      // Remove from invites
      setInvites(prev => prev.filter(inv => inv.id !== inviteId));
      
      // Add to ledgers
      setLedgers(prev => [ledger, ...prev]);
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to accept invite');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectInvite = async (inviteId: string) => {
    const invite = invites.find(inv => inv.id === inviteId);
    if (!invite) return;

    const token = await storage.getToken();
    if (!token) {
      Alert.alert('Error', 'Please login to continue');
      return;
    }

    try {
      setLoading(true);
      await ledgerApi.rejectInvite(token, inviteId);

      Alert.alert(
        'Invite Rejected',
        `You rejected the ledger invite from ${invite.fromUserName}.`,
        [{ text: 'OK' }]
      );

      setInvites(prev => prev.filter(inv => inv.id !== inviteId));
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to reject invite');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntryPress = () => {
    if (selectedTab === 'accounts') {
      navigation.navigate('CreateLedgerAccount');
    } else {
      // For transactions tab, we need a ledger selected
      // For now, show an alert to create from accounts tab
      Alert.alert(
        'Create Transaction',
        'Please create or open a ledger account first from the Accounts tab.',
        [{ text: 'OK' }]
      );
    }
  };

  let filteredEntries = entries;

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filteredEntries = filteredEntries.filter((entry: any) =>
      entry.note?.toLowerCase().includes(query) ||
      entry.personName?.toLowerCase().includes(query)
    );
  }

  const sortedEntries = filteredEntries;

  let filteredLedgers = ledgers;
  let filteredInvites: any[] = [];

  // Apply account search filter
  if (accountSearchQuery.trim()) {
    const query = accountSearchQuery.toLowerCase();
    filteredLedgers = filteredLedgers.filter((ledger: any) =>
      ledger.personName.toLowerCase().includes(query)
    );
  }

  const renderAccountsContent = () => {
    const hasLedgers = filteredLedgers.length > 0;
    const hasInvites = invites.length > 0;

    if (loading && ledgers.length === 0) {
      return (
        <View>
          <ListItemSkeleton />
          <ListItemSkeleton />
          <ListItemSkeleton />
          <ListItemSkeleton />
        </View>
      );
    }

    if (!hasLedgers && !hasInvites) {
      let message = 'No ledger accounts yet';
      if (selectedAccountFilter === 'bookmarked') message = 'No bookmarked ledgers';
      else if (selectedAccountFilter === 'shared') message = 'No shared ledgers';
      
      return <EmptyState icon="book-outline" message={message} />;
    }

    return (
      <Animated.FlatList
        data={filteredLedgers}
        keyExtractor={(item: any) => item.id}
        ListHeaderComponent={
          hasInvites ? (
            <View style={styles.invitesSection}>
              <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>
                Pending Invites ({invites.length})
              </ThemedText>
              {invites.map((invite: any) => (
                <PendingInviteCard
                  key={invite.id}
                  invite={invite}
                  onAccept={handleAcceptInvite}
                  onReject={handleRejectInvite}
                />
              ))}
              {hasLedgers && (
                <ThemedText style={[styles.sectionTitle, { color: theme.text, marginTop: 16 }]}>
                  Your Ledgers
                </ThemedText>
              )}
            </View>
          ) : null
        }
        renderItem={({ item }) => (
          <LedgerAccountCard
            ledger={item}
            onPress={() => handleLedgerPress(item.id, item.personName)}
            onBookmarkToggle={() => handleBookmarkToggle(item.id)}
          />
        )}
        contentContainerStyle={[styles.listContent, { paddingTop: headerHeight + 16, paddingBottom: 80 + insets.bottom }]}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
        ListFooterComponent={() => {
          if (!hasMore || filteredLedgers.length === 0) return null;
          
          return (
            <View style={styles.loadMoreContainer}>
              {loading ? (
                <ActivityIndicator size="small" color={theme.accent} />
              ) : (
                <TouchableOpacity
                  style={[styles.loadMoreButton, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
                  onPress={loadMoreLedgers}
                  activeOpacity={0.7}
                >
                  <ThemedText style={[styles.loadMoreText, { color: theme.accent }]}>Load More</ThemedText>
                  <Ionicons name="chevron-down" size={20} color={theme.accent} />
                </TouchableOpacity>
              )}
            </View>
          );
        }}
      />
    );
  };

  const renderTransactionsContent = () => {
    if (loading && entries.length === 0) {
      return (
        <View>
          <LedgerEntrySkeleton />
          <LedgerEntrySkeleton />
          <LedgerEntrySkeleton />
          <LedgerEntrySkeleton />
          <LedgerEntrySkeleton />
        </View>
      );
    }

    if (sortedEntries.length === 0) {
      const message = selectedFilter === 'all'
        ? 'No ledger entries yet'
        : `No ${selectedFilter} entries found`;
      return <EmptyState icon="receipt-outline" message={message} />;
    }

    return (
      <Animated.FlatList
        data={sortedEntries}
        keyExtractor={(item: any) => item.id}
        renderItem={({ item }) => (
          <LedgerEntryCard
            entry={{
              ...item,
              type: item.type === 'credit' ? 'received' : 'paid',
              amount: parseFloat(item.amount),
            }}
            onPress={() => handleEntryPress(item)}
          />
        )}
        contentContainerStyle={[styles.listContent, { paddingTop: headerHeight + 16, paddingBottom: 80 + insets.bottom }]}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
        ListFooterComponent={() => {
          if (!hasMore || sortedEntries.length === 0) return null;
          
          return (
            <View style={styles.loadMoreContainer}>
              {loading ? (
                <ActivityIndicator size="small" color={theme.accent} />
              ) : (
                <TouchableOpacity
                  style={[styles.loadMoreButton, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
                  onPress={handleLoadMore}
                  activeOpacity={0.7}
                >
                  <ThemedText style={[styles.loadMoreText, { color: theme.accent }]}>Load More</ThemedText>
                  <Ionicons name="chevron-down" size={20} color={theme.accent} />
                </TouchableOpacity>
              )}
            </View>
          );
        }}
      />
    );
  };

  const renderContent = () => {
    if (selectedTab === 'accounts') {
      return renderAccountsContent();
    }
    return renderTransactionsContent();
  };

  return (
    <AppShell title="Ledger" scrollable={false} showTopBar={false}>
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <Animated.View
          style={[styles.headerOverlay, { transform: [{ translateY: headerTranslate }] }]}
          onLayout={(event) => {
            const height = event.nativeEvent.layout.height;
            if (height !== headerHeight) {
              setHeaderHeight(height);
            }
          }}
        >
          <TopBar title="Ledger" showLogo={false} />
          <ScreenCommandHeader
          segments={[
            { value: 'accounts' as const, label: 'My Accounts' },
            { value: 'transactions' as const, label: 'All Transactions' },
          ]}
          selectedValue={selectedTab}
          onValueChange={setSelectedTab}
          searchConfig={{
            placeholder: selectedTab === 'accounts' ? 'Search ledger accounts...' : 'Search transactions...',
            value: selectedTab === 'accounts' ? accountSearchQuery : searchQuery,
            onChange: selectedTab === 'accounts' ? setAccountSearchQuery : setSearchQuery,
          }}
          actionComponent={
            <View style={styles.actionRow}>
              <TouchableOpacity
                style={[styles.filterButton, { backgroundColor: theme.backgroundTertiary }]}
                onPress={() => setShowFilterModal(true)}
                activeOpacity={0.7}
              >
                <Ionicons name="funnel-outline" size={20} color={theme.textTertiary} />
                {(selectedTab === 'transactions' && (filters.timeframe !== 'all' || filters.amountRange !== 'all' || filters.transactionType !== 'all')) ||
                 (selectedTab === 'accounts' && selectedAccountFilter !== 'all') ? (
                  <View style={[styles.filterBadge, { backgroundColor: theme.accent }]} />
                ) : null}
              </TouchableOpacity>
            </View>
          }
          footerContent={
            selectedTab === 'transactions' ? (
              <View style={styles.filterShortcuts}>
                <TouchableOpacity
                  style={[
                    styles.shortcutChip,
                    { 
                      backgroundColor: selectedFilter === 'all' ? theme.accent : theme.backgroundTertiary,
                      borderColor: selectedFilter === 'all' ? theme.accent : theme.border,
                    }
                  ]}
                  onPress={() => setSelectedFilter('all')}
                  activeOpacity={0.7}
                >
                  <ThemedText style={[
                    styles.shortcutText,
                    { color: selectedFilter === 'all' ? '#FFFFFF' : theme.textSecondary }
                  ]}>
                    All
                  </ThemedText>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.shortcutChip,
                    { 
                      backgroundColor: selectedFilter === 'received' ? theme.accent : theme.backgroundTertiary,
                      borderColor: selectedFilter === 'received' ? theme.accent : theme.border,
                    }
                  ]}
                  onPress={() => setSelectedFilter('received')}
                  activeOpacity={0.7}
                >
                  <ThemedText style={[
                    styles.shortcutText,
                    { color: selectedFilter === 'received' ? '#FFFFFF' : theme.textSecondary }
                  ]}>
                    Received
                  </ThemedText>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.shortcutChip,
                    { 
                      backgroundColor: selectedFilter === 'paid' ? theme.accent : theme.backgroundTertiary,
                      borderColor: selectedFilter === 'paid' ? theme.accent : theme.border,
                    }
                  ]}
                  onPress={() => setSelectedFilter('paid')}
                  activeOpacity={0.7}
                >
                  <ThemedText style={[
                    styles.shortcutText,
                    { color: selectedFilter === 'paid' ? '#FFFFFF' : theme.textSecondary }
                  ]}>
                    Paid
                  </ThemedText>
                </TouchableOpacity>
              </View>
            ) : null
          }
        />
        </Animated.View>

        <Animated.View
          style={[
            styles.content,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          {renderContent()}
        </Animated.View>
        <FloatingActionButton onPress={handleAddEntryPress} />

        {/* Filter Modal */}
        {selectedTab === 'transactions' ? (
          <LedgerFilters
            filters={filters}
            onFiltersChange={setFilters}
            visible={showFilterModal}
            onClose={() => setShowFilterModal(false)}
          />
        ) : (
          <AccountFilterModal
            selectedFilter={selectedAccountFilter}
            onFilterChange={setSelectedAccountFilter}
            visible={showFilterModal}
            onClose={() => setShowFilterModal(false)}
          />
        )}
      </View>
    </AppShell>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 10,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 999,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  filterBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  content: {
    flex: 1,
  },
  listContent: {
    paddingTop: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 100,
  },
  loadMoreContainer: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  loadMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 24,
    borderWidth: 1,
    gap: 8,
  },
  loadMoreText: {
    fontSize: 14,
    fontWeight: '600',
  },
  invitesSection: {
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginHorizontal: 16,
    marginVertical: 12,
  },
  filterShortcuts: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingTop: 12,
    gap: 8,
  },
  shortcutChip: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
  },
  shortcutText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default LedgerScreen;
